import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { getCard, getTheme } from '../../utils/cards';
import { initials } from '../../utils/theme';
import { useAuth } from '../../lib/auth';
import {
  CARD_MM, DEFAULT_DPI, DPI_OPTIONS, cardPixels, downloadBlob, loadPrintFonts, prepareLogoArt, preparePhotoArt,
  printAccent, printCardUrl, printFileName, svgBlob, svgToPng,
} from '../../utils/nfcPrint';
import AppHeader from '../AppHeader';
import Loading from '../Loading';
import { errorBox, noticeBox } from '../AuthLayout';
import { Section, Toggle } from '../EditorParts';
import { FRONT_VARIANTS, NfcCardMock, PHOTO_VARIANTS, QR_STYLES, buildBack, buildFront, useSvgUrl } from './NfcCardArt';

const btn = 'inline-flex items-center justify-center h-10 px-3.5 rounded-lg border border-line-strong bg-white text-sm font-semibold text-ink hover:bg-surface disabled:opacity-50';
const primaryBtn = 'inline-flex items-center justify-center h-10 px-4 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue-dark disabled:opacity-50';

/** /cards/:cardId/print — print-ready front and back art for a black NFC card. */
export default function NfcPrintPage() {
  const { cardId } = useParams();
  const location = useLocation();
  const { user, memberships, canManageOrg } = useAuth();

  const [card, setCard] = useState(null);
  const [theme, setTheme] = useState(null);
  const [org, setOrg] = useState(null); // { name, kind }
  const [fontCss, setFontCss] = useState(null);
  const [logo, setLogo] = useState(null); // prepared logo art, or null when there is none
  const [logoError, setLogoError] = useState('');
  const [photo, setPhoto] = useState(null); // prepared photo art, or null when the card has none
  const [photoError, setPhotoError] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [variant, setVariant] = useState('centered');
  const [qrStyle, setQrStyle] = useState('tile');
  const [logoPlate, setLogoPlate] = useState(false); // logo on a white background
  const [whiteOnly, setWhiteOnly] = useState(false);
  const [showSafe, setShowSafe] = useState(true);
  const [dpi, setDpi] = useState(DEFAULT_DPI);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [c, css] = await Promise.all([getCard(cardId), loadPrintFonts()]);
        if (!c) throw new Error('Card not found, or you do not have access to it.');
        const t = await getTheme(c.org_id);
        let o = memberships.find((m) => m.org_id === c.org_id)?.organizations;
        if (!o) {
          const { data } = await supabase.from('organizations').select('name, kind').eq('id', c.org_id).maybeSingle();
          o = data;
        }
        if (!active) return;
        setCard(c);
        setTheme(t);
        setOrg({ name: o?.name || '', kind: o?.kind || null });
        setFontCss(css);
        if (c.photo_url) {
          preparePhotoArt(c.photo_url)
            .then((art) => { if (active) setPhoto(art); })
            .catch((err) => {
              console.error('Photo prep failed:', err);
              if (active) setPhotoError('The profile photo could not be loaded, so the photo layouts are hidden.');
            });
        }
        if (t?.logo_url) {
          try {
            const art = await prepareLogoArt(t.logo_url);
            if (!active) return;
            setLogo(art);
            // Same colors as the digital card; colors that would vanish on black get a white background
            setLogoPlate(art.colorContrast < 2);
          } catch (err) {
            console.error('Logo prep failed:', err);
            if (active) setLogoError('The logo could not be loaded, so the front shows initials instead.');
          }
        }
      } catch (err) {
        if (active) setError(err.message || 'Could not load the card.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [cardId, memberships]);

  const allowed = card && (card.user_id === user.id || canManageOrg(card.org_id));
  const accent = useMemo(() => printAccent(theme), [theme]);
  const company = card ? card.company || org?.name || '' : '';
  // A raster logo with its own background prints as a rectangle: ask for a transparent one
  const needsTransparentLogo = !!logo && showLogo && logo.opaqueBackground && !logo.isSvg;
  const canEditBrand = card && canManageOrg(card.org_id);

  const frontArgs = (v) => ({
    variant: v,
    name: card?.full_name || '',
    title: card?.title || '',
    company,
    initials: initials(card?.full_name),
    logo: logo && showLogo
      ? whiteOnly
        ? { href: logo.white, aspect: logo.aspect } // one ink: the logo itself prints white
        : { href: logo.color, aspect: logo.aspect, plate: logoPlate }
      : null,
    photo: photo ? { href: whiteOnly ? photo.white : photo.original, aspect: photo.aspect } : null,
    accent: accent.color,
    whiteOnly,
    fontCss,
  });

  const front = useMemo(
    () => (card && fontCss !== null ? buildFront(frontArgs(variant)) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card, fontCss, variant, logo, showLogo, logoPlate, photo, accent, company, whiteOnly]
  );
  const back = useMemo(
    () => (card && fontCss !== null ? buildBack({ url: printCardUrl(card.slug), qrStyle, accent: accent.color, whiteOnly, fontCss }) : ''),
    [card, fontCss, qrStyle, accent, whiteOnly]
  );

  if (loading) return <Loading label="Preparing your card design..." />;

  const backTo = location.state?.from || (card ? `/cards/${card.id}/edit` : '/');
  if (error || !allowed) {
    return (
      <div className="min-h-screen bg-surface">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-4">
          <Link to="/" className="text-sm font-medium text-ink-muted hover:text-ink">← My cards</Link>
          <div className={errorBox}>{error || 'Only the cardholder and company admins can open this card\'s print design.'}</div>
        </main>
      </div>
    );
  }

  const { width: pw, height: ph } = cardPixels(dpi);
  // kind: 'png' (main file), 'white' (white underbase layer PNG) or 'svg'
  const download = async (side, kind) => {
    const svg = side === 'front' ? front : back;
    setBusy(`${side}-${kind}`);
    try {
      const blob = kind === 'svg' ? svgBlob(svg) : await svgToPng(svg, dpi, { whiteLayer: kind === 'white' });
      downloadBlob(blob, kind === 'white'
        ? printFileName(card.slug, side, 'png', 'white')
        : printFileName(card.slug, side, kind));
    } catch (err) {
      console.error(err);
      setError('The file could not be created. Please try again.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <Link to={backTo} className="text-sm font-medium text-ink-muted hover:text-ink">← Back</Link>
          <h1 className="text-[32px] leading-[38px] font-extrabold tracking-tight mt-1">NFC card design</h1>
          <p className="text-[15px] text-ink-muted mt-1">
            Print files for {card.full_name}'s black NFC card. Tapping the card or scanning its code opens{' '}
            <span className="font-mono text-sm text-ink">{printCardUrl(card.slug).replace(/^https:\/\//, '')}</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
          <div className="space-y-6 min-w-0">
            <div className="bg-white rounded-2xl border border-line p-5 sm:p-8 grid md:grid-cols-2 gap-6 sm:gap-8">
              <NfcCardMock svg={front} label="Front" showSafe={showSafe} />
              <NfcCardMock svg={back} label="Back" showSafe={showSafe} />
            </div>
            {card.status !== 'active' && (
              <p className={noticeBox}>This card is {card.status}. The NFC tap and QR code only open it once it is live.</p>
            )}

            <Section title="Download print files"
              hint={`Transparent background, exact card size (${CARD_MM.width} × ${CARD_MM.height} mm, no bleed). PNG is ${pw} × ${ph} px at ${dpi} DPI.`}>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm font-semibold text-ink">Resolution</span>
                <div role="radiogroup" aria-label="Resolution" className="inline-flex rounded-lg border border-line-strong overflow-hidden">
                  {DPI_OPTIONS.map((d) => (
                    <button key={d} type="button" role="radio" aria-checked={dpi === d} onClick={() => setDpi(d)}
                      className={`h-9 px-3 text-sm font-semibold ${dpi === d ? 'bg-navy text-white' : 'bg-white text-ink hover:bg-surface'}`}>
                      {d} DPI
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {['front', 'back'].map((side) => (
                  <div key={side} className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => download(side, 'png')} disabled={!!busy} className={primaryBtn} data-testid={`dl-${side}-png`}>
                      {busy === `${side}-png` ? 'Preparing...' : `${side === 'front' ? 'Front' : 'Back'} PNG`}
                    </button>
                    {!whiteOnly && (
                      <button type="button" onClick={() => download(side, 'white')} disabled={!!busy} className={btn} data-testid={`dl-${side}-white`}>
                        {busy === `${side}-white` ? 'Preparing...' : `${side === 'front' ? 'Front' : 'Back'} white layer`}
                      </button>
                    )}
                    <button type="button" onClick={() => download(side, 'svg')} disabled={!!busy}
                      className="inline-flex items-center h-10 px-2 text-sm font-semibold text-ink-muted hover:text-ink hover:underline disabled:opacity-50" data-testid={`dl-${side}-svg`}>
                      SVG
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-sm text-ink-muted mt-4 pt-4 border-t border-line space-y-2">
                <p>The PNG is the print file. The black in the preview is the card itself; the files only contain the ink, so the background stays clear.</p>
                <p>
                  Each file is exactly card size ({CARD_MM.width} × {CARD_MM.height} mm), so it can be centered on the card in the printer software.
                  Front and back are both landscape and the back is not rotated relative to the front.
                </p>
                {whiteOnly
                  ? <p>White ink only is on, so the PNG is already a single white layer.</p>
                  : <p><strong className="text-ink font-semibold">White layer</strong>: for printers that print a white base under colors. It's the same design in solid white, the same size and DPI as the PNG.</p>}
              </div>
            </Section>
          </div>

          <aside className="space-y-5 min-w-0">
            <Section title="Front layout" hint={photoError || null}>
              <div role="radiogroup" aria-label="Front layout" className="space-y-3">
                <div className="grid grid-cols-3 gap-2.5">
                  {FRONT_VARIANTS.map((v) => (
                    <VariantButton key={v.id} v={v} on={variant === v.id} onClick={() => setVariant(v.id)}
                      svg={card && fontCss !== null ? buildFront(frontArgs(v.id)) : ''} />
                  ))}
                </div>
                {photo && (
                  <>
                    <p className="text-xs font-semibold tracking-wider uppercase text-ink-muted pt-1">With your photo</p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {PHOTO_VARIANTS.map((v) => (
                        <VariantButton key={v.id} v={v} on={variant === v.id} onClick={() => setVariant(v.id)}
                          svg={card && fontCss !== null ? buildFront(frontArgs(v.id)) : ''} />
                      ))}
                    </div>
                    {!whiteOnly && <p className="text-sm text-ink-muted">Photos print in full color, so the printer needs a white base layer under them (the white layer download).</p>}
                  </>
                )}
              </div>
            </Section>

            <Section title="Logo" hint={logo ? null : logoError || 'No company logo yet, so the front shows initials.'}>
              {needsTransparentLogo && (
                <div className={`${noticeBox} mb-4`} role="status" data-testid="transparent-logo-notice">
                  <p className="font-semibold">This logo has a solid background, so Carduzo removes it for the card.</p>
                  <p className="mt-1">
                    For the cleanest edges, upload a transparent PNG or SVG.{' '}
                    {canEditBrand && org?.kind === 'company' ? (
                      <Link to={`/company/${card.org_id}?tab=brand`} className="font-semibold underline">Change it in Company → Brand</Link>
                    ) : canEditBrand ? (
                      'Upload one under Colors and logo in your card editor.'
                    ) : (
                      'Ask your company admin to upload one.'
                    )}
                  </p>
                </div>
              )}
              {logo ? (
                <div className="space-y-2">
                  <Toggle checked={showLogo} onChange={setShowLogo} label="Show the company logo" />
                  <p className="text-sm text-ink-muted pb-2">
                    {showLogo ? 'On the front of the card.' : photo ? 'Off: the classic layouts show initials, the photo layouts show no logo.' : 'Off: the front shows initials instead.'}
                  </p>
                  {showLogo && !whiteOnly && <Toggle checked={logoPlate} onChange={setLogoPlate} label="White background behind the logo" />}
                  {showLogo && <p className="text-sm text-ink-muted">
                    {whiteOnly
                      ? 'White ink only is on, so the logo prints in white.'
                      : logoPlate
                        ? 'The logo keeps its colors on a white rounded background, so it stands out on the black card.'
                        : logo.colorContrast < 2
                          ? 'This logo is very dark and will be hard to see on a black card. A white background is recommended.'
                          : 'Printed in its own colors, like on your digital card. Turn on for a white background if it needs more contrast.'}
                  </p>}
                </div>
              ) : null}
            </Section>

            <Section title="QR code" hint="On the back of the card. Phones can also just tap the card.">
              <div role="radiogroup" aria-label="QR code style" className="space-y-2">
                {QR_STYLES.map((s) => (
                  <button key={s.id} type="button" role="radio" aria-checked={qrStyle === s.id} onClick={() => setQrStyle(s.id)}
                    className={`w-full text-left p-3 rounded-xl transition ${qrStyle === s.id ? 'border-2 border-blue bg-blue-soft' : 'border border-line bg-white hover:border-line-strong'}`}>
                    <span className="block text-sm font-semibold text-ink">{s.name}{s.id === 'tile' && <span className="ml-2 text-xs font-semibold text-blue">Recommended</span>}</span>
                    <span className="block text-sm text-ink-muted mt-0.5">{s.description}</span>
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Colors">
              <div className="mb-4">
                <Toggle checked={whiteOnly} onChange={setWhiteOnly} label="White ink only" />
                <p className="text-sm text-ink-muted mt-1">The whole design in one color: accents, text and logo all print white{photo ? ', and the photo prints as a white one-color image' : ''}.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg border border-line shrink-0 flex items-center justify-center bg-[#111214]">
                  <span className="w-4 h-4 rounded-full" style={{ background: whiteOnly ? '#FFFFFF' : accent.color }} />
                </span>
                <p className="text-sm text-ink-muted">
                  {whiteOnly
                    ? 'Everything prints in white ink.'
                    : accent.fromBrand
                    ? 'Your brand color is light enough for a black card, so it is used for the accents.'
                    : 'Your brand colors are too dark to read on a black card, so the accents print in white.'}
                </p>
              </div>
            </Section>

            <Section title="Preview">
              <Toggle checked={showSafe} onChange={setShowSafe} label="Show safe zone" />
              <p className="text-sm text-ink-muted mt-2">Text and logos stay at least {CARD_MM.safe} mm from the edge. The dashed line isn't printed.</p>
            </Section>
          </aside>
        </div>
      </main>
    </div>
  );
}

function VariantButton({ v, on, onClick, svg }) {
  const url = useSvgUrl(svg);
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick} title={v.description}
      className={`p-2 rounded-xl flex flex-col gap-1.5 text-left text-[13px] font-semibold text-ink transition ${on ? 'border-2 border-blue bg-blue-soft' : 'border border-line bg-white hover:border-line-strong'}`}>
      <span className="block w-full rounded-[4px] bg-[#111214] overflow-hidden" style={{ aspectRatio: `${CARD_MM.width} / ${CARD_MM.height}` }}>
        {url && <img src={url} alt="" className="w-full h-full" />}
      </span>
      {v.name}
    </button>
  );
}
